import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Input,
  Button,
  Text,
  Spinner,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { Eye24Regular, EyeOff24Regular } from '@fluentui/react-icons';
import { usePortalAuth } from '../contexts/PortalAuthContext';
import '../styles/portal.css';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0078d4 0%, #004578 100%)',
  },
  card: {
    width: '100%',
    maxWidth: '400px',
    backgroundColor: '#ffffff',
    ...shorthands.borderRadius('12px'),
    ...shorthands.padding('40px'),
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.2)',
  },
  logo: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '32px',
  },
  logoImage: {
    height: '48px',
  },
  title: {
    textAlign: 'center' as const,
    fontSize: '24px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    marginBottom: '8px',
  },
  subtitle: {
    textAlign: 'center' as const,
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
    marginBottom: '32px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: tokens.colorNeutralForeground1,
  },
  error: {
    backgroundColor: '#fde7e9',
    ...shorthands.border('1px', 'solid', '#f3d6d8'),
    ...shorthands.borderRadius('4px'),
    ...shorthands.padding('12px'),
    color: '#a80000',
    fontSize: '14px',
  },
  forgotPassword: {
    textAlign: 'right' as const,
    fontSize: '14px',
    color: tokens.colorBrandForeground1,
    cursor: 'pointer',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  footer: {
    marginTop: '24px',
    textAlign: 'center' as const,
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
  },
  link: {
    color: tokens.colorBrandForeground1,
    cursor: 'pointer',
    ':hover': {
      textDecoration: 'underline',
    },
  },
});

const Login: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading } = usePortalAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const from = (location.state as any)?.from?.pathname || '/portal';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email || !password) {
      setError('Please enter your email and password');
      return;
    }

    try {
      await login({ email, password });
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.logo}>
          <img
            src="/logo-tradewinds.png"
            alt="Tradewinds Mechanical"
            className={styles.logoImage}
          />
        </div>

        <Text as="h1" className={styles.title}>
          Customer Portal
        </Text>
        <br />
        <Text className={styles.subtitle}>
          Sign in to manage your orders and account
        </Text>

        <form className={styles.form} onSubmit={handleSubmit}>
          {error && (
            <div className={styles.error} role="alert">
              {error}
            </div>
          )}

          <div className={styles.field}>
            <label className={styles.label} htmlFor="portal-login-email">Email</label>
            <Input
              id="portal-login-email"
              type="email"
              value={email}
              onChange={(e, data) => setEmail(data.value)}
              placeholder="you@example.com"
              size="large"
              disabled={isLoading}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="portal-login-password">Password</label>
            <Input
              id="portal-login-password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e, data) => setPassword(data.value)}
              placeholder="Enter your password"
              size="large"
              disabled={isLoading}
              contentAfter={
                <Button
                  appearance="transparent"
                  icon={showPassword ? <EyeOff24Regular /> : <Eye24Regular />}
                  onClick={() => setShowPassword(!showPassword)}
                  size="small"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                />
              }
            />
          </div>

          <Text className={styles.forgotPassword}>
            Forgot password?
          </Text>

          <Button
            appearance="primary"
            type="submit"
            size="large"
            disabled={isLoading}
          >
            {isLoading ? <Spinner size="tiny" /> : 'Sign In'}
          </Button>
        </form>

        <div className={styles.footer}>
          <Text>
            Need help? <span className={styles.link}>Contact Support</span>
          </Text>
        </div>
      </div>
    </div>
  );
};

export default Login;
